/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 * NavbarService layer between component and Content provider service to handle all filtering
 * and data manipulation logic for global navigation data
 */

// It is not considering import as declaration , throwing lint error at import statement itself

import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Content } from '@core/aem/models/content.interface';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { MarketingDataEmitterInterface } from '@root/shared/interfaces/marketing.interface';
import { Router, NavigationEnd } from '@angular/router';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { ContentItem } from '@core/aem/models/content-item.interface';
import { map, takeUntil } from 'rxjs/operators';

/**
 * Service layer between component and Content provider service to handle all filtering
 * and data manipulation logic for global navigation data
 * @docs-private
 */
@Injectable()
export class MarketingService {
  // tslint:disable:no-any
  private marketingInformationSubject: Subject<MarketingDataEmitterInterface>;
  private marketingShownSubject: Subject<boolean>;
  public marketingTemplates: ContentItem<any>[];
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private contentProviderService: ContentProviderService,
    private router: Router
  ) {
    this.marketingShownSubject = new Subject<boolean>();
    this.marketingInformationSubject = new Subject<MarketingDataEmitterInterface>();
    this.marketingShownSubject.next(false);

    this.contentProviderService.receivedContent.subscribe((content: Content) => {
      this.marketingInformationSubject.next(this.getIncomingMarketingData(content));
    });
    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
      const currentRoute: string = this.router.url;
      if (event instanceof NavigationEnd && !(currentRoute === '/' || currentRoute === '/home')) {
        this.marketingInformationSubject.next({ show: false });
      }
    });
  }
  public getContentData(): Observable<ConfigData> {
    return this.contentProviderService.getContentData(null, 'prepaid').pipe(
      map(
        (contentData: ConfigData) => {
          return contentData;
        },
        () => {
          this.router.navigate([SHARED_CONSTANTS.ERROR_404_ROUTE]);
        }
      )
    );
  }

  public send(content: Content): void {
    this.marketingInformationSubject.next(this.getIncomingMarketingData(content));
  }

  private getIncomingMarketingData(response: Content): MarketingDataEmitterInterface {
    const emittedData: MarketingDataEmitterInterface = { show: false, data: {} };
    let enhancements = [];
    if (
      response &&
      response.model &&
      response.model['model-data'] &&
      response.model['model-data']['module-enhancements']
    ) {
      // Full Response
      if (Array.isArray(response.model['model-data']['module-enhancements'])) {
        enhancements = response.model['model-data']['module-enhancements'];
      }
    } else if (Array.isArray(response)) {
      // Module enhancements: response of getContentData
      enhancements = response;
    }
    this.marketingTemplates = enhancements.filter((enhancement: any) => {
      return enhancement.uiComponent === 'marketing-template';
    });
    if (this.marketingTemplates.length > 0 && this.marketingTemplates[0].data) {
      emittedData.show = true;
      emittedData.data = this.marketingTemplates[0].data;
    }
    return emittedData;
  }

  public watch(): Observable<MarketingDataEmitterInterface> {
    return this.marketingInformationSubject.asObservable();
  }
  /**
   * function to listen zip modal cancellation event
   * @input showModalData: boolean
   * @returns void
   */
  public updateMarketingInformationSubject(showModalData: boolean): void {
    if (this.marketingTemplates.length && this.marketingTemplates[0].data) {
      const emittedData: MarketingDataEmitterInterface = {
        show: showModalData,
        data: this.marketingTemplates[0].data,
      };
      this.marketingInformationSubject.next(emittedData);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
